import {
  CardType,
  type ClozeCard,
  type InlineNode,
  type Cloze,
  type EnWordCard,
  ClozeCardFaceType,
  EnWordCardFaceType,
  KidHanziCardFaceType,
  type ClozeCardFace,
  type EnWordCardFace,
  type KidHanziCardFace,
  type ChoiceFace,
  type GiveAwayChoiceFace,
  type MinimalChoiceFace,
  type Card,
  type ParsedClozeCard,
  type MCQCardFace,
} from '@/types/core'
import type { Content, P, StringOrHanzi } from '@/types/model'
import { Card as CardZod } from '@/types/zod'
import { cloneDeep, filter, shuffle } from 'lodash-es'
import { warn } from '@/utils/pure'
import type { JSONContent } from '@tiptap/core'
import type { BlockNode, KidHanziCard, MCQCard } from '@/types/model'
import type { ClozeGroup } from '@/components/DistractorPanel/DistractorPanel.vue'
import { getAllIndexedClozes } from '@/components/ConcreteCard/ClozeCardFace/faces/Choice/helper'
import type { CardResponse } from '@/api/package-source'

export function getContentClozes(content: Content): Cloze[] {
  const clozes: Cloze[] = []

  for (const blockNode of content) {
    if (blockNode.type === 'p') {
      clozes.push(
        ...(blockNode.content.filter(n => n.type === 'cloze') as Cloze[])
      )
    }
  }

  return clozes
}

export function flatContent(content: Content): InlineNode[] {
  const nodes: InlineNode[] = []

  for (const blockNode of content) {
    nodes.push(...blockNode.content)
  }

  return nodes
}

export function removeContentNode(content: Content, n: InlineNode): Content {
  for (const blockNode of content) {
    const result: InlineNode[] = []
    for (const inlineNode of blockNode.content) {
      if (inlineNode !== n) {
        result.push(inlineNode)
      }
    }

    blockNode.content = result
  }

  return content
}

export function trimContent(content: Content): Content {
  if (content.length === 0) return []

  const result = cloneDeep(content)

  function stringifyParagraph(p: P): string {
    const texts: string[] = []

    for (const n of p.content) {
      if (n.type === 'text') {
        texts.push(n.text)
      } else if (n.type === 'cloze') {
        const options = [n.text, ...(n.distrators ?? [])]
        texts.push(`{{${n.group}::${options.join('|')}}}`)
      }
    }

    return texts.join('')
  }

  // 先删除前面的空段落
  while (result.length > 0) {
    const p = result[0]

    if (p.content.length === 0 || stringifyParagraph(p).trim() === '') {
      result.shift()
    } else {
      break
    }
  }

  // 再删除后面的空段落
  while (result.length > 0) {
    const p = result[result.length - 1]

    if (p.content.length === 0 || stringifyParagraph(p).trim() === '') {
      result.pop()
    } else {
      break
    }
  }

  return result
}

export function parseClozeCard(card: ClozeCard): {
  card: ParsedClozeCard
  error?: string
} {
  const result: ParsedClozeCard = {
    type: CardType.CLOZE,
    content: [],
    altContents: [],
    analysis: [],
    illustration: card.illustration?.trim(),
  }

  if (card.analysis) {
    result.analysis = trimContent(card.analysis)
  }

  try {
    if (typeof card.content === 'string') {
      return {
        card: result,
        error: '知识点卡片内容格式错误',
      }
    } else {
      result.content = trimContent(card.content)
    }

    const contentClozes = getContentClozes(card.content)

    if (card.altContents) {
      for (const content of card.altContents) {
        if (typeof content === 'string') {
          return {
            card: result,
            error: '知识点卡片内容改述格式错误',
          }
        }
        const parsedClozes = getContentClozes(content)

        // 如果两边的挖空数量不一致，则解析失败
        if (contentClozes.length !== parsedClozes.length) {
          throw new Error('[内容改述]中的挖空数量和[内容]中的挖空数量不匹配')
        }

        // 将解析后的挖空依次替换成 content 中相同位置的挖空
        for (
          let blockNodeIdx = 0;
          blockNodeIdx < content.length;
          blockNodeIdx++
        ) {
          const blockNode = content[blockNodeIdx]
          if (blockNode.type === 'p') {
            for (let i = 0; i < blockNode.content.length; i++) {
              const parsedNode = blockNode.content[i]

              if (parsedNode.type === 'cloze') {
                const sourceBlockNode = card.content[blockNodeIdx]

                blockNode.content[i] = sourceBlockNode.content[i]
              }
            }
          }
        }

        result.altContents.push(trimContent(content))
      }
    }

    if (typeof card.analysis === 'string') {
      return {
        card: result,
        error: '知识点卡片内容内容改述格式错误',
      }
    }

    return { card: result }
  } catch (_err: any) {
    warn(_err)
    return { card: result, error: _err.toString() }
  }
}

export function parseEnWordCard(card: EnWordCard): {
  card: EnWordCard
  error?: string
} {
  const result: EnWordCard = {
    type: CardType.EN_WORD,
    word: card.word.trim(),
    definition: card.definition.trim(),
    prons: card.prons.map(item => ({
      label: item.label.trim(),
      language: item.language,
    })),
    examples: card.examples.map(item => ({
      source: item.source.trim(),
      translation: item.translation.trim(),
    })),
    distrators: card.distrators.map(item => ({
      word: item.word.trim(),
      definition: item.definition.trim(),
    })),
    tabs: card.tabs.map(item => ({
      title: item.title.trim(),
      content: trimContent(item.content),
    })),
    illustration: card.illustration?.trim(),
  }

  return { card: result }
}

export function parseMCQCard(card: MCQCard): {
  card: MCQCard
  error?: string
} {
  const result: MCQCard = {
    type: CardType.MCQ,
    question: card.question.trim(),
    options: card.options.map(item => ({
      content: item.content.trim(),
      isAnswer: item.isAnswer,
    })),
    analysis: card.analysis?.trim(),
    illustration: card.illustration?.trim(),
  }

  return { card: result }
}

export function parseCard(card: Card) {
  switch (card.type) {
    case CardType.CLOZE:
      return parseClozeCard(card)
    case CardType.EN_WORD:
      return parseEnWordCard(card)
    case CardType.MCQ:
      return parseMCQCard(card)
    case CardType.KID_HANZI:
      return { card }
    default:
      throw new Error(`unrechable code`)
  }
}

// 目录树卡片摘要显示
export function getCardTextDigest(content: string): string {
  const cardData = JSON.parse(content) as Card
  let result = ''

  switch (cardData.type) {
    case CardType.CLOZE:
      const card = parseClozeCard(cardData).card

      result =
        card == null
          ? _t('cardview.parse_failed')
          : contentToString(card.content).join('\n')
      return result.trim()
    case CardType.EN_WORD:
      result = cardData.word
      break
    case CardType.MCQ:
      result = cardData.question
      break
    case CardType.KID_HANZI:
      result = cardData.hanzi[0]?.[1]
      break
    default:
      return _t('cardview.card_type_not_support')
  }
  // 原数据中的换行符，渲染成空格
  return result.replace(/\n/g, ' ')
}

// content 转成 string 数组，数组中每一项代表每一个段落
export function contentToString(content: Content): string[] {
  const result: string[] = []

  for (const blockNode of content) {
    if (blockNode.type === 'p') {
      result.push(blockNode.content.map(inlineNodeToString).join(''))
    }
  }

  return result
}
// InlineNode => string
export function inlineNodeToString(node: InlineNode): string {
  if (node.type === 'cloze') {
    // 挖空部分在两端增加 []
    return `[${node.text}]`
  }
  if (node.type === 'text') {
    return node.text
  }
  return ''
}

export function isClozeTextTooLong(text: string) {
  const MAX_CHAR_LENGTH = 12
  let chineseLength = 0
  let englishLength = 0

  for (let i = 0; i < text.length; i++) {
    if (
      /[\u4e00-\u9fa5\u3040-\u309f\u30a0-\u30ff\u1100-\u11ff\u3130-\u318F\uAC00-\uD7AF]/.test(
        text[i]
      )
    ) {
      // 汉字字符（包括中文、日文、韩文）
      chineseLength++
    } else {
      // 英文字符（除了汉字之外的）
      englishLength++
    }
  }

  return chineseLength * 2 + englishLength > MAX_CHAR_LENGTH
}

const symbols = [
  // 全角符号
  '！',
  '＂',
  '＃',
  '＄',
  '％',
  '＆',
  '＇',
  '（',
  '）',
  '＊',
  '＋',
  '，',
  '－',
  '．',
  '／',
  '：',
  '；',
  '＜',
  '＝',
  '＞',
  '？',
  '＠',
  '［',
  '＼',
  '］',
  '＾',
  '＿',
  '｀',
  '｛',
  '｜',
  '｝',
  '～',
  '《',
  '》',
  '“',
  '”',
  '‘',
  '’',
  '【',
  '】',
  '￥',

  // 半角符号
  '!',
  '"',
  '#',
  '$',
  '%',
  '&',
  "'",
  '(',
  ')',
  '*',
  '+',
  ',',
  '-',
  '.',
  '/',
  ':',
  ';',
  '<',
  '=',
  '>',
  '?',
  '@',
  '[',
  '\\',
  ']',
  '^',
  '_',
  '`',
  '{',
  '|',
  '}',
  '~',
]

function hasNoSymbol(input: string) {
  return new RegExp(`[^${symbols.join('')}]`, 'gm').test(input)
}

// 解析一个填空题的内容是不是问答题的类型，同时返回「问题」部分
// eg: 中国第一部浪漫主义诗歌总集是：{{楚辞}} 属于问答题
//   问题: 中国第一部浪漫主义诗歌总集是
export function parseQAContent(content: Content): {
  isQA: boolean
  qaContent: Content
} {
  const clozes = getContentClozes(content)

  if (clozes.length !== 1) {
    return {
      isQA: false,
      qaContent: content,
    }
  }

  const clone = cloneDeep(content)
  const beforeContent: Content = []
  const afterContent: Content = []
  let cloze: Cloze | null = null

  for (const blockNode of clone) {
    let newBlockNode: BlockNode = {
      type: blockNode.type,
      content: [],
    }

    for (const inlineNode of blockNode.content) {
      if (inlineNode.type === 'cloze') {
        cloze = inlineNode as Cloze

        beforeContent.push(newBlockNode)
        newBlockNode = {
          type: blockNode.type,
          content: [],
        }
        continue
      }

      newBlockNode.content.push(inlineNode)
    }

    if (cloze == null) {
      beforeContent.push(newBlockNode)
    } else {
      afterContent.push(newBlockNode)
    }
  }

  const beforeContentString = stringifyContent(beforeContent)
  const afterContentString = stringifyContent(afterContent)

  return {
    isQA: hasNoSymbol(beforeContentString) && !hasNoSymbol(afterContentString),
    qaContent: removeTailSymbols(beforeContent),
  }
}

function removeTailSymbols(content: Content): Content {
  // 从末尾开始找，每找到一个符号就移除掉，同时需要注意空行
  const reversedContent = [...content].reverse()
  const filteredContent: Content = []
  let symbolReplaced = false

  for (const blockNode of reversedContent) {
    // 如果是空行，则不需要添加进内容区域
    if (blockNode.content.length === 0) {
      continue
    }

    filteredContent.unshift(blockNode)

    // 如果已经把挖空前的符号都去除了，则不需要再处理了
    if (symbolReplaced) continue

    const reversedNodes = [...blockNode.content].reverse()

    for (const inlineNode of reversedNodes) {
      if (inlineNode.type === 'text') {
        inlineNode.text = inlineNode.text.replace(
          new RegExp(`([${symbols.join('')}]|\\s)*$`, 'gm'),
          ''
        )

        if (hasNoSymbol(inlineNode.text)) {
          symbolReplaced = true
        }
      }
    }
  }

  return filteredContent
}

export function processClozeFace(face: ClozeCardFace) {
  const clozes = getContentClozes(face.card.content)
  const distrators = clozes.reduce<string[]>(
    (acc, cur) => acc.concat(cur.distrators || []),
    []
  )

  if (
    [
      ClozeCardFaceType.Choice,
      ClozeCardFaceType.GiveAwayChoice,
      ClozeCardFaceType.MinimalChoice,
    ].includes(face.type)
  ) {
    face = face as ChoiceFace | GiveAwayChoiceFace | MinimalChoiceFace

    if (clozes.length > 1) {
      face.style.operationLayout = 'horizontal'
    } else if (clozes.length === 1) {
      const cloze = clozes[0]
      const options = [cloze.text].concat(
        (face.type === ClozeCardFaceType.GiveAwayChoice
          ? cloze.giveAwayDistrators
          : cloze.distrators) ?? []
      )

      const hasTooLongOption = options.some(item => isClozeTextTooLong(item))
      if (hasTooLongOption) {
        face.style.operationLayout = 'vertical'
      } else {
        face.style.operationLayout = 'grid'
      }
    }
  }

  const allClozeDistrators = face.altCards.reduce((acc, cur) => {
    const clozes = getContentClozes(cur.content)

    for (const cloze of clozes) {
      acc = acc.concat(cloze.distrators || [])
    }

    return acc
  }, distrators)

  // fallback 逻辑: https://qianmo.atlassian.net/browse/LD-670
  // 如果是送分选择题卡面，当挖空数不为 1 或者是不存在送分干扰项时，fallback 到 MinimalChoice
  if (face.type === ClozeCardFaceType.GiveAwayChoice) {
    if (
      clozes.length !== 1 ||
      (clozes[0].giveAwayDistrators ?? []).length === 0
    ) {
      face = {
        ...face,
        type: ClozeCardFaceType.MinimalChoice,
      }
    }
  }

  if (
    face.type === ClozeCardFaceType.MinimalChoice ||
    face.type === ClozeCardFaceType.Choice
  ) {
    if (clozes.length > 1) {
      face.altCards.length = 0
      return face
    }

    const validAltCards = face.altCards.filter(card => {
      return getContentClozes(card.content).length > 0
    })

    // 当卡片仅有一个挖空且本身没有干扰项，且 altCards 中没有可以当作干扰项的卡片时 fallback
    if (
      distrators.length === 0 &&
      validAltCards.length === 0 &&
      clozes.length === 1
    ) {
      face = {
        ...face,
        type: ClozeCardFaceType.Judgement,
      }
    }
  } else if (face.type === ClozeCardFaceType.Judgement) {
    const validAltCards = face.altCards.filter(card => {
      return getContentClozes(card.content).length > 0
    })

    if (allClozeDistrators.length === 0 && validAltCards.length === 0) {
      face = {
        ...face,
        type: ClozeCardFaceType.GiveAwayJudgement,
      }
    }
  }

  return face
}
export function processEnWordFace(face: EnWordCardFace) {
  if (face.type === EnWordCardFaceType.PickImage) {
    const validAltCards = face.altCards.filter(card => {
      return card.illustration != null && card.illustration.trim() !== ''
    })

    if (
      face.card.illustration == null ||
      face.card.illustration.trim() === '' ||
      validAltCards.length < 3
    ) {
      face = {
        ...face,
        type: EnWordCardFaceType.WordChoice,
      }
    } else {
      face.altCards = validAltCards
    }
  }

  if (face.type === EnWordCardFaceType.ExampleChoice) {
    if (face.card.examples.length === 0) {
      face = {
        ...face,
        type: EnWordCardFaceType.WordChoice,
      }
    }
  }

  return face
}

export function processMCQFace(face: MCQCardFace) {
  return face
}

export function stringifyContent(content: Content): string {
  const texts: string[] = []

  for (const blockNode of content) {
    if (blockNode.type === 'p') {
      for (const n of blockNode.content) {
        if (n.type === 'text') {
          texts.push(n.text)
        } else if (n.type === 'cloze') {
          const options = [n.text, ...(n.distrators ?? [])]
          texts.push(`{{${n.group}::${options.join('|')}}}`)
        }
      }
    }
  }

  return texts.join('')
}

export function newClozeCard(): ClozeCard {
  return {
    type: CardType.CLOZE,
    content: [{ type: 'p', content: [] }],
    altContents: [],
    analysis: [],
  }
}

export function newWordCard(): EnWordCard {
  return {
    type: CardType.EN_WORD,
    word: '',
    definition: '',
    examples: [],
    distrators: [],
    tabs: [
      {
        title: '近义词',
        content: [],
      },
      {
        title: '易混淆',
        content: [],
      },
      {
        title: '词组',
        content: [],
      },
    ],
    prons: [
      {
        label: '',
        language: 'en-US',
      },
      {
        label: '',
        language: 'en-GB',
      },
    ],
  }
}

export function newMCQCard(optionsLen = 2): MCQCard {
  return {
    type: CardType.MCQ,
    question: '',
    options: Array.from({ length: optionsLen }).map((_, i) => {
      return {
        content: '',
        isAnswer: i === 0,
      }
    }),
  }
}

export function newKidHanziCard(): KidHanziCard {
  return {
    type: CardType.KID_HANZI,
    hanzi: [],
    analysis: [],
  }
}

export function isEmptyCard(card: Card | string | CardDraft): boolean {
  try {
    // 处理空值
    if (!card) return true

    // 如果传入的是 string 类型，尝试解析为 Card 对象
    if (typeof card === 'string') {
      if (!card.trim()) return true // 空字符串直接返回 true
      const parsedCard = JSON.parse(card) as Card
      return isEmptyValue(parsedCard)
    }

    // 处理 CardDraft 类型（判断是否包含 content 字符串属性）
    if (
      typeof card === 'object' &&
      'renderId' in card &&
      typeof card.content === 'string'
    ) {
      // 尝试解析 content 字段
      const parsedCard = JSON.parse(card.content) as Card
      return isEmptyValue(parsedCard)
    }

    // 处理常规 Card 对象
    return isEmptyValue(card)
  } catch (error) {
    // JSON 解析失败或其他错误，视为无效卡片
    console.error('Error processing card:', error)
    return true
  }
}

function isEmptyValue(value: any): boolean {
  // 处理基本类型
  if (value === null || value === undefined) return true
  if (typeof value === 'string') return value.trim() === ''
  if (typeof value === 'number') return false // 数字不视为空
  if (typeof value === 'boolean') return false // 布尔值不视为空

  // 处理数组 - 空数组或所有元素为空
  if (Array.isArray(value)) {
    return value.length === 0 || value.every(isEmptyValue)
  }

  // 处理对象 - 所有关键字段为空
  if (typeof value === 'object') {
    // 忽略一些辅助性属性，比如 type, isAnswer 等
    const skipProps = ['type', 'isAnswer', 'style', 'group', 'language']

    // 过滤掉不需要检查的属性
    const keyProps = Object.keys(value).filter(key => !skipProps.includes(key))

    // 对于特殊类型的卡片处理
    if (value.type === CardType.MCQ) {
      // MCQ 需要检查是否有答案
      const hasAnswer =
        Array.isArray(value.options) &&
        value.options.some((opt: { isAnswer: any }) => opt.isAnswer)
      if (!hasAnswer) return true
    }

    // 对象所有关键属性都为空才认为对象为空
    return (
      keyProps.length === 0 || keyProps.every(key => isEmptyValue(value[key]))
    )
  }

  return false
}

export function validateCard(card: Card): string {
  switch (card.type) {
    case CardType.CLOZE: {
      const isContentEmpty = stringifyContent(card.content) === ''
      const hasCloze = getContentClozes(card.content).length > 0

      if (isContentEmpty) {
        return _t('cardview.validate_tip7')
      }

      if (!hasCloze) {
        return _t('cardview.validate_tip8')
      }
      break
    }

    case CardType.EN_WORD: {
      if (card.word.trim() === '') {
        return _t('cardview.validate_tip2')
      }
      if (card.definition.trim() === '') {
        return _t('cardview.validate_tip3')
      }
      break
    }

    case CardType.MCQ: {
      if (card.question.trim() === '') {
        return _t('cardview.validate_tip9')
      }

      if (card.options.some(item => item.content.trim() === '')) {
        return _t('cardview.validate_tip10')
      }

      if (card.options.every(item => !item.isAnswer)) {
        return _t('cardview.validate_tip11')
      }
      break
    }

    case CardType.KID_HANZI: {
      if (card.hanzi.length !== 1 || card.hanzi[0][0] !== 'hanzi') {
        return _t('cardview.validate_kid_hanzi_1')
      }

      if (card.analysis.length === 0) {
        return _t('cardview.validate_kid_hanzi_2')
      }

      if (card.analysis.length > 50) {
        return _t('cardview.validate_kid_hanzi_4')
      }
    }
  }

  // 优先判断自定义的错误信息，卡片结构作为保底检查放在最后面
  // safety check，编辑器应该保证不出现这个问题
  if (!CardZod.safeParse(card).success) {
    return _t('cardview.card_wrong')
  }

  return ''
}

export function content2Doc(content: Content) {
  const paragraphs = content.map(blockNode => {
    const pContent = blockNode.content.map(n => {
      switch (n.type) {
        case 'text': {
          const marks: {
            type: string
            attrs?: Record<string, any>
            [key: string]: any
          }[] = []

          if (n.style?.dot) {
            marks.push({
              type: 'textStyle',
              attrs: {
                dot: true,
              },
            })
          }

          return {
            type: 'text',
            text: n.text,
            marks,
          }
        }

        case 'cloze':
          return {
            type: 'text',
            text: n.text,
            marks: [
              {
                type: 'cloze',
                attrs: {
                  group: n.group,
                  distrators: n.distrators,
                  giveAwayDistrators: n.giveAwayDistrators,
                },
              },
            ],
          }
      }
    })
    return {
      type: 'p',
      content: pContent,
    }
  })

  return {
    type: 'doc',
    content: paragraphs,
  } as JSONContent
}

// 编辑器给出来的干扰项可能是个空字符串，这里需要用该方法过滤
function ensureDistratorsType(distrators: string[]) {
  return Array.isArray(distrators) ? distrators : undefined
}

export function doc2Content(doc: JSONContent): Content {
  if (doc.content == null) return []

  return doc.content.map(p => {
    return {
      type: 'p',
      content:
        p.content?.map(n => {
          if (n.type === 'text') {
            const marks = n.marks ?? []
            const clozeMark = marks.find(item => item.type === 'cloze')

            if (clozeMark != null) {
              return {
                type: 'cloze',
                text: n.text!,
                group: clozeMark.attrs?.group,
                distrators: ensureDistratorsType(clozeMark.attrs?.distrators),
                giveAwayDistrators: ensureDistratorsType(
                  clozeMark.attrs?.giveAwayDistrators
                ),
              }
            } else {
              const styleMark = marks.find(item => item.type === 'textStyle')

              return {
                type: 'text',
                text: n.text!,
                style: {
                  dot: styleMark?.attrs?.dot,
                },
              }
            }
          }
        }) ?? [],
    } as BlockNode
  })
}

// 移除卡片中用户未填写但是必填的字段
export function removeCardEmptyFields(card: Card): Card {
  switch (card.type) {
    case CardType.CLOZE:
      return {
        ...card,
        illustration: card.illustration?.trim() || undefined,
      }
    case CardType.EN_WORD:
      return {
        type: card.type,
        word: card.word,
        definition: card.definition,
        illustration: card.illustration?.trim() || undefined,
        prons: filter(
          card.prons,
          (item: EnWordCard['prons'][number]) => item.label.trim() !== ''
        ),
        examples: filter(
          card.examples,
          (item: EnWordCard['examples'][number]) =>
            item.source.trim() !== '' && item.translation.trim() !== ''
        ),
        distrators: filter(
          card.distrators,
          (item: EnWordCard['distrators'][number]) =>
            item.word.trim() !== '' && item.definition.trim() !== ''
        ),
        tabs: filter(
          card.tabs,
          (item: EnWordCard['tabs'][number]) =>
            item.title.trim() !== '' &&
            stringifyContent(item.content).trim() !== ''
        ),
      }
    case CardType.MCQ:
      return {
        ...card,
        illustration: card.illustration?.trim() || undefined,
      }
    case CardType.KID_HANZI:
      return {
        ...card,
        illustration: card.illustration?.trim() || undefined,
      }
  }
}

// 卡片文本两端加「」文本字符超过 20 字时截断+...
export function getCardTitleForDialog(content: string): string {
  const title = getCardTextDigest(content)
  return `「${title.length > 20 ? title.slice(0, 20) + '...' : title}」`
}

export function countClozeGroup(content: Content): number {
  const allClozes = getContentClozes(content)
  const groupSet = new Set<string>()
  let groupCount = 0

  for (const cloze of allClozes) {
    if (!groupSet.has(cloze.group)) {
      groupCount++
      groupSet.add(cloze.group)
    }
  }

  return groupCount
}

export function updateClozeCardContentDistractors(
  card: ClozeCard,
  group: ClozeGroup,
  distractors: string[]
): Content {
  const newContent = cloneDeep(card.content)

  const allClozes = getAllIndexedClozes(newContent)

  // 干扰项存在组内第一个知识点上，并清空组内其他知识点的干扰项
  for (let i = 0; i < group.clozes.length; i++) {
    const cloze = group.clozes[i]
    const targetCloze = allClozes.find(item => item.index === cloze.index)

    if (targetCloze == null) return newContent

    if (i === 0) {
      targetCloze.distrators = distractors
    } else {
      targetCloze.distrators = []
    }
  }

  for (const cloze of allClozes) {
    delete (cloze as any)['index']
  }

  return newContent
}

export function isValidClozeGroup(group: string) {
  return /^c[1-9]\d*$/.test(group)
}

export function parseClozeGroupIndex(group: string): number | undefined {
  if (!isValidClozeGroup(group)) return

  return parseInt(group.slice(1))
}

export type CardDraft = CardResponse & {
  id?: number
  renderId: number
}

// 处理汉字卡片的解析和挖空逻辑
export function processKidHanziFace(face: KidHanziCardFace): KidHanziCardFace {
  if (face.type === KidHanziCardFaceType.ClozeChoice) {
    // 检查解析是否为空或不包含当前汉字
    const analysisText = stringifyKidHanziContent(face.card.analysis)
    const currentHanzi = face.card.hanzi[0]?.[1]

    if (
      !analysisText ||
      !currentHanzi ||
      analysisText.includes(currentHanzi) === false ||
      analysisText.trim() === currentHanzi.trim() // 解析和汉字本身完全相同，fallback
    ) {
      // fallback 到 VoiceChoice
      return {
        ...face,
        type: KidHanziCardFaceType.VoiceChoice,
      }
    }
  }

  return face
}

// 将StringOrHanzi数组转换为纯文本字符串
export function stringifyKidHanziContent(content: StringOrHanzi[]): string {
  return content
    .map(item => {
      if (typeof item === 'string') {
        return item
      } else if (Array.isArray(item) && item[0] === 'hanzi') {
        return item[1]
      }
      return ''
    })
    .join('')
}

// 从解析中提取当前汉字的挖空位置（只挖单个字）
export function extractHanziClozePositions(
  analysis: StringOrHanzi[],
  targetHanzi: string
): Array<{ start: number; end: number; text: string }> {
  const text = stringifyKidHanziContent(analysis)
  const positions: Array<{ start: number; end: number; text: string }> = []

  let textIndex = 0

  for (let i = 0; i < analysis.length; i++) {
    const item = analysis[i]
    let itemText = ''

    if (typeof item === 'string') {
      itemText = item
    } else if (Array.isArray(item) && item[0] === 'hanzi') {
      itemText = item[1]
    }

    // 查找当前项中所有目标汉字的位置
    let searchIndex = 0
    while (searchIndex < itemText.length) {
      const foundIndex = itemText.indexOf(targetHanzi, searchIndex)
      if (foundIndex === -1) break

      // 只挖单个字，不考虑连续情况
      positions.push({
        start: textIndex + foundIndex,
        end: textIndex + foundIndex + targetHanzi.length,
        text: targetHanzi, // 始终只是单个目标汉字
      })

      searchIndex = foundIndex + targetHanzi.length
    }

    textIndex += itemText.length
  }

  return positions
}

// 生成汉字填空的选项（只处理单个字）
export function generateHanziClozeOptions(
  card: KidHanziCard,
  altCards: KidHanziCard[],
  clozeText: string
): Array<{ text: string; isAnswer: boolean; used: boolean; wrong: boolean }> {
  const answerOption = {
    text: clozeText,
    isAnswer: true,
    used: false,
    wrong: false,
  }

  let distractors: Array<{
    text: string
    isAnswer: boolean
    used: boolean
    wrong: boolean
  }> = []

  // 从altCards中提取干扰项（只取第一个字）
  altCards.forEach(altCard => {
    const altHanzi = altCard.hanzi[0]?.[1]
    if (altHanzi && altHanzi !== clozeText) {
      // 只取第一个字作为干扰项
      const firstChar = altHanzi.charAt(0)
      if (firstChar && firstChar !== clozeText) {
        distractors.push({
          text: firstChar,
          isAnswer: false,
          used: false,
          wrong: false,
        })
      }
    }
  })

  // 干扰项去重，且不与答案重复
  distractors = distractors.filter(
    (d, idx, arr) =>
      d.text !== clozeText && arr.findIndex(x => x.text === d.text) === idx
  )

  // 如果干扰项不够，添加一些随机汉字（单个字）
  let fallbackTries = 0
  while (distractors.length < 1 && fallbackTries < 10) {
    const randomHanzi = generateRandomHanzi(1) // 只生成单个字
    if (
      randomHanzi !== clozeText &&
      !distractors.some(d => d.text === randomHanzi)
    )
 {
      distractors.push({
        text: randomHanzi,
        isAnswer: false,
        used: false,
        wrong: false,
      })
    }
    fallbackTries++
  }

  // 如果依然不够，fallback 到 VoiceChoice（由上层处理）
  if (distractors.length < 1) {
    throw new Error('Not enough unique distractors for Hanzi Cloze')
  }

  // 随机选择1个干扰项
  const selectedDistractors = shuffle(distractors).slice(0, 1)

  return shuffle([answerOption, ...selectedDistractors])
}

// 生成随机汉字（简单实现）
function generateRandomHanzi(length: number): string {
  const commonHanzi =
    '的一是在不了有和人这中大为上个国我以要他时来用们生到作地于出就分对成会可主发年动同工也能下过子说产种面而方后多定行学法所民得经十三之进着等部度家电力里如水化高自二理起小物现实加量都两体制机当使点从业本去把性好应开它合还因由其些然前外天政四日那社义事平形相全表间样与关各重新线内数正心反你明看原又么利比或但质气第向道命此变条只没结解问意建月公无系军很情者最立代想已通并提直题党程展五果料象员革位入常文总次品式活设及管特件长求老头基资边流路级少图山统接知较将组见计别她手角期根论运农指几九区强放决西被干做必战先回则任取据处队南给色光门即保治北造百规热领七海口东导器压志世金增争济阶油思术极交受联什认六共权收证改清己美再采转单风切打白教速花带安场身车例真务具万每目至达走积示议声报斗完类八离华名确才科张信马节话米整空元况今集温传土许步群广石记需段研界拉林律叫且究观越织装影算低持音众书布复容儿须际商非验连断深难近矿千周委素技备半办青省列习响约支般史感劳便团往酸历市克何除消构府称太准精值号率族维划选标写存候毛亲快效斯院查江型眼王按格养易置派层片始却专状育厂京识适属圆包火住调满县局照参红细引听该铁价严龙飞'
  let result = ''
  for (let i = 0; i < length; i++) {
    result += commonHanzi[Math.floor(Math.random() * commonHanzi.length)]
  }
  return result
}
